import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { Button, Empty, Form, Spin } from 'antd'
import { FormItemInputContext } from 'antd/es/form/context'
import { RightOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useCompanyDimensionTree } from '../../../../features/dimension/queries/useCompanyDimensionTree'
import { Dimension } from '../../../../features/dimension/types/Dimension'
import { FilterPanel } from '../FilterPanel'
import CheckboxTree, { StringTitleTreeNode } from '../CheckboxTree'
import CustomCheckboxGroup from '../CustomCheckboxGroup'
import { useDebounce } from '../../../../utils/hooks'
import { CompanySelection } from './types'

interface CompanyOption {
  id: string
  name: string
}

interface CompanyDimensionPickerProps {
  companies: CompanyOption[]
  value?: CompanySelection[]
  onChange?: (value: CompanySelection[]) => void
  disabled?: boolean
  /** Passed by Form.Item — prevents error styling from leaking into inner inputs */
  id?: string
}

const toTreeData = (dimensions: Dimension[]): StringTitleTreeNode[] =>
  dimensions.map(d => ({
    key: d.dimensionId,
    title: d.name,
    children: d.children ? toTreeData(d.children) : undefined
  }))

const panelStyle: React.CSSProperties = {
  flex: 1,
  padding: 8,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column'
}

const DimensionPanel: React.FC<{
  companyId: string
  selectedDimensions: string[]
  onDimensionsChange: (dimensions: string[]) => void
}> = ({ companyId, selectedDimensions, onDimensionsChange }) => {
  const { t } = useTranslation()
  const { data: dimensionTree, isLoading } = useCompanyDimensionTree(companyId)
  const [dimSearch, setDimSearch] = useState('')
  const debouncedDimSearch = useDebounce(dimSearch, 300)

  const treeData = useMemo(() => {
    if (!dimensionTree) return []
    return toTreeData(dimensionTree)
  }, [dimensionTree])

  const allCount = useMemo(() => {
    const count = (nodes: StringTitleTreeNode[]): number =>
      nodes.reduce((sum, n) => sum + 1 + count(n.children ?? []), 0)
    return count(treeData)
  }, [treeData])

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin />
      </div>
    )
  }

  if (treeData.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Empty description={t('global:no-results')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    )
  }

  return (
    <FilterPanel
      searchText={dimSearch}
      onSearchChange={setDimSearch}
      selectedCount={selectedDimensions.length}
      totalCount={allCount}
      onClear={() => onDimensionsChange([])}
      placeholder={t('global:searchDimension')}
    >
      <CheckboxTree
        treeData={treeData}
        value={selectedDimensions}
        onChange={onDimensionsChange}
        searchText={debouncedDimSearch}
        shiftClickMode="sameLevel"
      />
    </FilterPanel>
  )
}

const CompanyDimensionPicker: React.FC<CompanyDimensionPickerProps> = ({
  companies,
  value = [],
  onChange,
  disabled,
  id
}) => {
  const { t } = useTranslation()
  const { status } = Form.Item.useStatus()
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(() => value[0]?.id ?? null)
  const [hasSeeded, setHasSeeded] = useState<boolean>(() => value.length > 0)
  const [companySearch, setCompanySearch] = useState('')
  const debouncedCompanySearch = useDebounce(companySearch, 300)

  // Seed active company once the form hydrates value asynchronously.
  // Runs only the first time value becomes non-empty, so manual navigation
  // (including toggling the active company off) is preserved afterwards.
  useEffect(() => {
    if (!hasSeeded && value.length > 0) {
      setActiveCompanyId(prev => prev ?? value[0].id)
      setHasSeeded(true)
    }
  }, [value, hasSeeded])

  const selectedMap = useMemo(() => {
    const map = new Map<string, CompanySelection>()
    value.forEach(cs => map.set(cs.id, cs))
    return map
  }, [value])

  const selectedIds = useMemo(() => value.map(cs => cs.id), [value])

  const handleNavigate = useCallback((companyId: string) => {
    setActiveCompanyId(prev => (prev === companyId ? null : companyId))
  }, [])

  const companyOptions = useMemo(() => companies.map(c => ({ value: c.id, label: c.name })), [companies])

  const renderCompanyExtra = useCallback(
    (companyId: string) => {
      return (
        <Button
          size="small"
          variant="filled"
          color={activeCompanyId === companyId ? 'primary' : 'default'}
          onClick={e => {
            e.stopPropagation()
            handleNavigate(companyId)
          }}
          icon={<RightOutlined />}
        />
      )
    },
    [activeCompanyId, handleNavigate]
  )

  const handleCompanyChange = useCallback(
    (newSelectedIds: string[]) => {
      const existing = new Map(value.map(cs => [cs.id, cs]))

      const next = newSelectedIds.map(
        companyId =>
          existing.get(companyId) ?? { id: companyId, name: companies.find(c => c.id === companyId)?.name ?? '' }
      )

      onChange?.(next)
    },
    [value, onChange, companies]
  )

  const handleDimensionsChange = useCallback(
    (dimensions: string[]) => {
      if (!activeCompanyId) return
      const dims = dimensions.length > 0 ? dimensions : undefined

      if (selectedMap.has(activeCompanyId)) {
        const next = value.map(cs => (cs.id === activeCompanyId ? { ...cs, dimensions: dims } : cs))
        onChange?.(next)
      } else {
        const company = companies.find(c => c.id === activeCompanyId)
        onChange?.([...value, { id: activeCompanyId, name: company?.name ?? '', dimensions: dims }])
      }
    },
    [activeCompanyId, value, onChange, selectedMap, companies]
  )

  const activeSelection = activeCompanyId ? selectedMap.get(activeCompanyId) : undefined

  return (
    <div
      id={id}
      style={{
        display: 'flex',
        border: `1px solid ${status === 'error' ? '#ff4d4f' : '#d9d9d9'}`,
        borderRadius: 6,
        height: 400,
        width: '100%',
        ...(disabled ? { opacity: 0.5, pointerEvents: 'none' as const } : {})
      }}
    >
      <FormItemInputContext.Provider value={{}}>
        {/* Left panel — company list */}
        <div style={{ ...panelStyle, borderRight: '1px solid #d9d9d9' }}>
          <FilterPanel
            searchText={companySearch}
            onSearchChange={setCompanySearch}
            selectedCount={value.length}
            totalCount={companies.length}
            onClear={() => onChange?.([])}
            placeholder={t('global:searchCompany')}
          >
            <CustomCheckboxGroup
              options={companyOptions}
              value={selectedIds}
              activeValue={activeCompanyId}
              onChange={handleCompanyChange}
              searchText={debouncedCompanySearch}
              renderExtra={renderCompanyExtra}
            />
          </FilterPanel>
        </div>

        {/* Right panel — dimensions for active company */}
        <div style={panelStyle}>
          {activeCompanyId ? (
            <DimensionPanel
              companyId={activeCompanyId}
              selectedDimensions={activeSelection?.dimensions ?? []}
              onDimensionsChange={handleDimensionsChange}
            />
          ) : (
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}
            >
              {t('customReportPage:clickCompanyToSelectDimensions')}
            </div>
          )}
        </div>
      </FormItemInputContext.Provider>
    </div>
  )
}

export default CompanyDimensionPicker
